/* eslint-disable max-lines */
import { Alert, InputField } from '@hakimo-ui/shared/ui-base';
import { SelectMenu, ImageUpload } from '@hakimo-ui/hakimo/ui-elements';
import {
  ProfileFormFieldsProps,
  ProfileImageUploadProps,
  ModalAlertsProps,
} from '@hakimo-ui/hakimo/types';
import {
  GENDER_OPTIONS,
  MIN_AGE,
  WatchStatusToggle,
  handleImageUpload,
  handleImageRemove,
} from './utils';
import { PROFILE_TYPE_FILTER_OPTIONS } from './types';

export function ProfileFormFields({
  formState,
  valueUpdater,
  showBadgeId = false,
}: ProfileFormFieldsProps & { showBadgeId?: boolean }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <InputField
          type="text"
          value={formState.first_name}
          onChange={valueUpdater('first_name')}
          label="First Name"
          placeholder="Enter first name..."
          required
        />
        <InputField
          type="text"
          value={formState.last_name}
          onChange={valueUpdater('last_name')}
          label="Last Name"
          placeholder="Enter last name..."
        />
        <InputField
          type="text"
          value={showBadgeId ? formState.badge_id || '' : formState.employee_id}
          onChange={valueUpdater(showBadgeId ? 'badge_id' : 'employee_id')}
          label={showBadgeId ? 'Badge ID' : 'Employee ID'}
          placeholder={
            showBadgeId ? 'Enter badge ID...' : 'Enter employee ID...'
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <InputField
          type="number"
          value={formState.age}
          onChange={valueUpdater('age')}
          label="Age"
          placeholder="Enter age..."
          min={MIN_AGE.toString()}
        />
        <div className="mt-1 w-full">
          <SelectMenu
            label="Gender"
            value={formState.gender}
            items={GENDER_OPTIONS}
            onChange={(value) => value && valueUpdater('gender')(value)}
            displayValue={(item?: string) => item || ''}
          />
        </div>
        <div className="mt-1 w-full">
          <SelectMenu
            label="Profile Type"
            value={PROFILE_TYPE_FILTER_OPTIONS.find(
              (option) => option.name === formState.profile_type
            )}
            items={PROFILE_TYPE_FILTER_OPTIONS}
            onChange={(option) =>
              option && valueUpdater('profile_type')(option.name)
            }
            displayValue={(item) => item?.label || ''}
            id={(item) => item.id}
          />
        </div>
      </div>

      <WatchStatusToggle
        enabled={formState.is_enabled}
        onChange={(checked: boolean) => valueUpdater('is_enabled')(checked)}
      />
    </>
  );
}

export function ProfileImageUpload({
  imagePreview,
  setImagePreview,
  setFormState,
  isRequired = false,
  setValidationState,
  label = 'Primary Image',
  imageType = 'primary',
}: ProfileImageUploadProps) {
  const onImageChange = (file: File) => {
    handleImageUpload(
      file,
      setFormState,
      setImagePreview,
      setValidationState,
      imageType
    );
  };

  const onImageRemove = () => {
    handleImageRemove(
      setImagePreview,
      setFormState,
      setValidationState,
      imageType
    );
  };

  return (
    <ImageUpload
      imagePreview={imagePreview}
      onImageChange={onImageChange}
      onImageRemove={onImageRemove}
      isRequired={isRequired}
      label={label}
    />
  );
}

export function ModalAlerts({
  validationState,
  mutation,
  imageUploadMutation,
}: ModalAlertsProps) {
  return (
    <>
      {!validationState.isValid && (
        <div className="overflow-hidden">
          <Alert type="warning">{validationState.message}</Alert>
        </div>
      )}
      {mutation.isError && (
        <div className="overflow-hidden">
          <Alert type="error">{mutation.error.message}</Alert>
        </div>
      )}
      {imageUploadMutation?.isError && (
        <div className="overflow-hidden">
          <Alert type="error">
            Primary image upload failed
            {imageUploadMutation.error.message
              ? `: ${imageUploadMutation.error.message}`
              : ''}
          </Alert>
        </div>
      )}
    </>
  );
}
